import { useEffect, useState } from 'react'
import styles from './ScoopFileDirectory.module.scss'
import {
    FolderIcon,
    FolderPlusIcon,
    SquaresFourIcon,
    ListBulletsIcon,
    ArrowLeftIcon,
    FileIcon,
} from '@phosphor-icons/react'
import { ScoopButton, ScoopText, ScoopTextField, ScoopDialog } from '../../ui'

export const ScoopFileDirectory = ({
    files,
    selectedFile,
    setSelectedFile,
    setPath,
    isSaving,
    path,
}) => {
    const [currentPath, setCurrentPath] = useState(path || '/')
    const [fileView, setFileView] = useState('list')
    const [searchQuery, setSearchQuery] = useState('')
    const [openModal, setOpenModal] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')
    const pathLevel = currentPath === '/' ? 0 : currentPath.split('/').length

    useEffect(() => {
        if (setPath) setPath(currentPath)
    }, [currentPath, setPath])

    const selectedFileName = files.find((f) => f.id === selectedFile)?.name

    const getCurrentPathElements = () => {
        let pathElements = files.filter((f) => f.path.startsWith(currentPath))
        if (searchQuery.trim() !== '') {
            pathElements = pathElements.filter((f) =>
                f.name?.toLowerCase().includes(searchQuery.toLowerCase())
            )
        }
        const pathFiles = pathElements.filter((f) => f.path === currentPath)
        const pathFolders = pathElements
            .filter((f) => f.path.startsWith(currentPath) && f.path.length > currentPath.length)
            .map((f) => f.path.split('/')[currentPath === '/' ? 1 : pathLevel])
            .filter((f, i, a) => a.indexOf(f) === i)
        return [...pathFolders, ...pathFiles]
    }

    const handleFolderSelection = (folderName) => {
        setCurrentPath((state) => state + (state === '/' ? folderName : '/' + folderName))
        setSelectedFile('')
    }

    const renderCurrentPathElements = () => {
        const elements = getCurrentPathElements()
        return elements.map((element) => {
            if (typeof element === 'string') {
                return (
                    <div
                        key={element}
                        className={`${styles.folderElement} ${
                            fileView === 'list' ? styles.listFolder : styles.squaresFolder
                        }`}
                        onDoubleClick={() => handleFolderSelection(element)}
                    >
                        <FolderIcon size={fileView === 'list' ? 20 : 40} weight="regular" />
                        <ScoopText
                            className={
                                fileView === 'list'
                                    ? styles.listFolderText
                                    : styles.squaresFolderText
                            }
                        >
                            {element}
                        </ScoopText>
                    </div>
                )
            } else {
                if (!isSaving) {
                    return (
                        <div
                            key={element.id}
                            className={`${styles.fileElement} ${
                                selectedFile === element.id ? styles.fileElementSelected : ''
                            } ${fileView === 'list' ? styles.listFile : styles.squaresFile}`}
                            onClick={() => setSelectedFile(element.id)}
                        >
                            <FileIcon size={fileView === 'list' ? 20 : 40} weight="regular" />
                            <ScoopText
                                className={
                                    fileView === 'list'
                                        ? styles.listFileText
                                        : styles.squaresFileText
                                }
                            >
                                {element.name}
                            </ScoopText>
                        </div>
                    )
                } else return null
            }
        })
    }

    const handleBack = () => {
        const newPath = currentPath.split('/')
        newPath.splice(-1)
        setCurrentPath(newPath.length === 1 ? '/' : newPath.join('/'))
        setSelectedFile(null)
    }

    const handleCreateFolder = () => {
        if (newFolderName.trim() === '') return
        const newFolderPath = currentPath + (currentPath === '/' ? '' : '/') + newFolderName
        setCurrentPath(newFolderPath)
        setOpenModal(false)
        setNewFolderName('')
    }

    return (
        <div className={styles.fileDirectoryContainer}>
            <div className={styles.fileDirectoryContent}>
                <div className={styles.fileDirectoryHeader}>
                    <div className={styles.fileDirectorySearch}>
                        <div className={styles.searchInputContainer}>
                            <button
                                className={styles.filesViewMode}
                                disabled={currentPath === '/'}
                                onClick={handleBack}
                            >
                                <ArrowLeftIcon size={20} weight="regular" />
                            </button>
                            <ScoopTextField
                                disableMargin
                                placeholder="Search files"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ height: 30 }}
                            />
                        </div>
                        <div className={styles.viewModeButtons}>
                            {isSaving && (
                                <button
                                    onClick={() => setOpenModal(true)}
                                    className={styles.filesViewMode}
                                >
                                    <FolderPlusIcon size={20} weight="regular" />
                                </button>
                            )}
                            <button
                                onClick={() => setFileView('squares')}
                                className={`${styles.filesViewMode} ${
                                    fileView === 'squares' ? styles.filesViewModeSelected : ''
                                }`}
                            >
                                <SquaresFourIcon size={20} weight="regular" />
                            </button>
                            <button
                                onClick={() => setFileView('list')}
                                className={`${styles.filesViewMode} ${
                                    fileView === 'list' ? styles.filesViewModeSelected : ''
                                }`}
                            >
                                <ListBulletsIcon size={20} weight="regular" />
                            </button>
                        </div>
                    </div>
                    <div className={styles.pathDisplay}>
                        <ScoopText>
                            {currentPath}
                            {selectedFileName ? '/' + selectedFileName : ''}
                        </ScoopText>
                    </div>
                </div>
                <div
                    className={`${styles.fileDirectoryFiles} ${
                        fileView === 'list' ? styles.listView : styles.squaresView
                    }`}
                >
                    {renderCurrentPathElements()}
                </div>
            </div>
            <ScoopDialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                title="Create New Folder"
                actions={
                    <div className={styles.dialogActions}>
                        <ScoopButton variant="grey" size="sm" onClick={() => setOpenModal(false)}>
                            Cancel
                        </ScoopButton>
                        <ScoopButton variant="default" size="sm" onClick={handleCreateFolder}>
                            Create
                        </ScoopButton>
                    </div>
                }
            >
                <ScoopTextField
                    label="Folder Name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                />
            </ScoopDialog>
        </div>
    )
}
